// Types
import type { EmissionFactorType } from "@/features/BuildingProject";

/**
 * Pure helpers that compute the next `EmissionFactorType` shape emitted by
 * `EmissionConsumptionChoice` for each user action. These are exported so
 * they can be unit-tested in isolation (the component itself wires them up
 * to props/refs and the `update:modelValue` event).
 *
 * Mutual-exclusivity invariant (per row): at most one of `sourceId` or
 * `customSourceId` is set. Custom sources are created and edited inside
 * `CustomDataSourcesModal` — the data-source picker only ever selects from
 * already-persisted rows, so there is no inline-create payload to track.
 */

const emptyModel = (): EmissionFactorType => ({
  sourceId: null,
  customSourceId: undefined,
  customSource: undefined,
  description: undefined,
  type: "",
  value: null,
});

/**
 * Built-in `emission_factor_source.id` was picked. Clear any prior custom
 * selection so the mutual-exclusivity invariant holds.
 */
export function selectBuiltInSource(modelValue: EmissionFactorType, sourceId: string): EmissionFactorType {
  return {
    ...modelValue,
    sourceId,
    customSourceId: undefined,
    customSource: undefined,
  };
}

/**
 * Existing custom row (already persisted, has an id) was picked. Clear the
 * built-in `sourceId` and snap `type` to the custom row's stored
 * `emissionFactorType` — custom sources are persisted with a single supply
 * type, so the supply-type dropdown is locked to that value while the custom
 * source is selected (the component disables the dropdown accordingly).
 */
export function selectExistingCustomSource(
  modelValue: EmissionFactorType,
  customSourceId: string,
  emissionFactorType: EmissionFactorType["type"]
): EmissionFactorType {
  return {
    ...modelValue,
    sourceId: null,
    customSourceId,
    customSource: undefined,
    type: emissionFactorType,
  };
}

/**
 * Combobox cleared / reset. Mirrors the existing reset-on-empty payload that
 * call sites have always seen (`type` and `value` are cleared too).
 */
export function clearSource(): EmissionFactorType {
  return emptyModel();
}
